import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';

import {ActionCode} from '@app/models';
import {PermissionsService} from '@app/services/permissions.service';

/**
 * Guard de ruta por permiso. Lee `data.referenceCode` y `data.action`:
 *   - sin code → deja pasar (no gatea rutas no migradas).
 *   - con code y sin action → evalúa `hasAccess` (≥1 acción; retrocompat).
 *   - con code y action → evalúa `hasPermission(code, action)` (p.ej. action:'CONSULT').
 *   En ambos casos espera a que carguen los permisos (ready$, evita falso bloqueo en
 *   carga directa por URL); si no tiene acceso, redirige a '/'.
 */

@Injectable({providedIn: 'root'})
export class PermissionGuard implements CanActivate {
  constructor(
    private permissionsService: PermissionsService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const referenceCode = route.data?.['referenceCode'] as string | undefined;
    const action = route.data?.['action'] as ActionCode | undefined;
    if (!referenceCode) {
      return of(true);
    }
    return this.permissionsService.ready$.pipe(
      filter(ready => ready),
      take(1),
      map(() => {
        const allowed = action
          ? this.permissionsService.hasPermission(referenceCode, action)
          : this.permissionsService.hasAccess(referenceCode);
        return allowed ? true : this.router.parseUrl('/');
      }),
    );
  }
}
